import React, { useState, useEffect, ReactElement, useRef } from "react";
import clsx from "clsx";

import { ReactComponent as IconCaret } from "../../../assets/svg/caret_up.svg";
import { ReactComponent as IconCheck } from "../../../assets/svg/check.svg";
import useOnClickOutside from "../../../hooks/useOnClickOutside";

import styles from "./Dropdown.module.scss";
export type Option = {
  value: number;
  label: string | ReactElement;
  data?: any;
};

export enum Variant {
  normal = "normal",
  blackTransparent = "blackTransparent",
}

export type Props = {
  options: Option[];
  placeholder?: string;
  defaultValue?: Option;
  variant?: Variant;
  className?: string;
  classNameOptions?: string;
  onChange: (value: number, data?: any) => void;
  value?: number;
};

const TEXT_NO_DATA = "データがありません";

export const Dropdown: React.FC<Props> = ({
  options,
  placeholder,
  defaultValue,
  value,
  variant = Variant.normal,
  className,
  classNameOptions,
  onChange,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const isControlled = value !== undefined;

  const getInitialOption = (): Option => {
    if (isControlled) {
      const match = options.find(opt => opt.value === value);
      if (match) return match;
    } else if (defaultValue) {
      return defaultValue;
    } else if (placeholder) {
      return { value: -1, label: placeholder };
    } else if (options.length > 0) {
      return options[0];
    }
    return { value: -1, label: TEXT_NO_DATA };
  };

  const [optionSelected, setOptionSelected] = useState<Option>(
    getInitialOption,
  );

  useEffect(() => {
    if (isControlled) {
      const match = options.find(opt => opt.value === value);
      if (match) {
        setOptionSelected(match);
      }
    }
  }, [value, options, isControlled]);

  const handleClickOutside = () => setOpen(false);
  useOnClickOutside(ref, handleClickOutside);

  const handleSelect = (option: Option) => {
    if (!isControlled) {
      setOptionSelected(option);
    }
    onChange(option.value, option.data);
    setOpen(false);
  };

  const labelChange = (option: Option) => {
    const found = options.find(o => o.value === option.value);
    return found ? option.label : placeholder ?? TEXT_NO_DATA;
  };

  return (
    <div
      ref={ref}
      className={clsx(styles.wrapSelect, styles[variant], className)}
      onClick={() => setOpen(!open)}
    >
      <div className={styles.option}>
        <div className={styles.label}>{labelChange(optionSelected)}</div>
        <IconCaret />
      </div>
      {open && options.length > 0 && (
        <div className={clsx(styles.options, classNameOptions)}>
          {options.map((item, index) => (
            <div
              className={clsx(
                styles.option,
                item.value === optionSelected.value && styles.active,
              )}
              onClick={() => handleSelect(item)}
              key={index}
            >
              <div className={styles.label}>{item.label}</div>
              <IconCheck />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
